import { baseApi } from './baseApi';
import { formatPubSubDate } from './personaPubsubUtils';

export const TAG_PERSONA_SNAPSHOTS = 'PersonaSnapshots';

/** UUID at the end of path `.../snapshots/<uuid>/` (full API URL or relative). */
const SNAPSHOT_UUID_IN_PATH =
  /\/snapshots\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?(?:\?|$)/i;

function extractUuidFromSnapshotUrl(url) {
  if (typeof url !== 'string') return null;
  const m = url.match(SNAPSHOT_UUID_IN_PATH);
  return m ? m[1] : null;
}

/** Normalize list API response (DRF, custom keys, array of bare UUIDs). */
function normalizeListResponse(response) {
  if (response == null) return [];
  if (Array.isArray(response)) return response;
  if (response?.results && Array.isArray(response.results)) return response.results;
  if (response?.snapshots && Array.isArray(response.snapshots)) return response.snapshots;
  if (response?.persona_snapshots && Array.isArray(response.persona_snapshots)) {
    return response.persona_snapshots;
  }
  if (response?.items && Array.isArray(response.items)) return response.items;
  if (response?.data && Array.isArray(response.data)) return response.data;
  /* axios-style */
  if (response?.data?.results && Array.isArray(response.data.results)) return response.data.results;
  /* single detail object instead of a list */
  if (typeof response === 'object' && (response.uuid || response.snapshot_uuid)) return [response];
  return [];
}

function coerceListRow(row) {
  if (row == null) return null;
  if (typeof row === 'string') return { uuid: row };
  if (typeof row === 'object') return row;
  return null;
}

/** DRF / Postgres JSONField may return object or JSON string. */
function normalizePayloadObject(payload) {
  if (payload == null) return null;
  if (typeof payload === 'object' && !Array.isArray(payload)) return payload;
  if (typeof payload === 'string') {
    const s = payload.trim();
    if (!s) return null;
    try {
      const parsed = JSON.parse(s);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Persona body text: backend exposes it in `payload.markdown` (alongside context / version / warnings).
 * Root `markdown` may stay empty while job runs; after completion the full body is often only in payload.
 */
function extractPersonaMarkdown(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const payloadObj = normalizePayloadObject(raw.payload);
  const fromPayload =
    typeof payloadObj?.markdown === 'string'
      ? payloadObj.markdown
      : typeof raw.payload?.markdown === 'string'
        ? raw.payload.markdown
        : '';
  const rootMd = typeof raw.markdown === 'string' ? raw.markdown : '';
  const a = rootMd.trim();
  const b = fromPayload.trim();
  if (a && b) {
    return b.length >= a.length ? fromPayload : rootMd;
  }
  if (a) return rootMd;
  if (b) return fromPayload;
  const fallback = raw.content ?? raw.prompt ?? raw.persona_markdown ?? raw.persona_text ?? '';
  return typeof fallback === 'string' ? fallback : '';
}

/** Scope counters: `scope_*` on root or `payload.context.scope`. */
function extractScopeDocumentCounts(raw) {
  const labIds =
    raw.scope_lab_document_ids ??
    raw.payload?.context?.scope?.lab_document_ids ??
    raw.lab_document_ids;
  const diagIds =
    raw.scope_diag_document_ids ??
    raw.payload?.context?.scope?.diag_document_ids ??
    raw.diag_document_ids;
  const labN = Array.isArray(labIds) ? labIds.length : undefined;
  const diagN = Array.isArray(diagIds) ? diagIds.length : undefined;
  return { labN, diagN };
}

/**
 * Use UUID (hyphenated string) in URLs, not numeric DRF `id`,
 * when the backend routes only by UUID — otherwise GET .../snapshots/1/ may 404.
 */
function extractSnapshotUuid(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const merged = {
    ...raw,
    ...(typeof raw.persona_snapshot === 'object' && raw.persona_snapshot != null
      ? raw.persona_snapshot
      : {}),
    ...(typeof raw.snapshot === 'object' && raw.snapshot != null ? raw.snapshot : {}),
  };
  const fromUrl =
    extractUuidFromSnapshotUrl(merged.url) ??
    extractUuidFromSnapshotUrl(merged.snapshot_url) ??
    extractUuidFromSnapshotUrl(merged.detail) ??
    extractUuidFromSnapshotUrl(merged.href);
  const candidates = [
    merged.snapshot_uuid,
    merged.uuid,
    typeof merged.snapshot_id === 'string' ? merged.snapshot_id : null,
    fromUrl,
  ].filter((x) => x != null && String(x).trim() !== '');
  const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  for (const c of candidates) {
    const s = String(c).trim();
    if (uuidLike.test(s)) return s;
  }
  if (candidates.length > 0) return String(candidates[0]).trim();
  if (merged.id != null && merged.id !== '') return String(merged.id);
  if (merged.pk != null && merged.pk !== '') return String(merged.pk);
  return null;
}

/** Map snapshot fields — aligned with GET /medapp/persona/snapshots/ (and detail). */
export function parsePersonaSnapshot(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const uuid = extractSnapshotUuid(raw);
  if (uuid == null || uuid === '') return null;
  const merged = {
    ...raw,
    ...(typeof raw.persona_snapshot === 'object' && raw.persona_snapshot != null
      ? raw.persona_snapshot
      : {}),
    ...(typeof raw.snapshot === 'object' && raw.snapshot != null ? raw.snapshot : {}),
  };
  const { labN, diagN } = extractScopeDocumentCounts(merged);
  return {
    uuid,
    createdAt: merged.created_at ?? merged.created ?? merged.generated_at ?? null,
    summary: merged.summary ?? merged.summary_text ?? merged.description ?? '',
    markdown: extractPersonaMarkdown(merged),
    processStatus: merged.process_status ?? merged.processStatus ?? null,
    isActive: Boolean(merged.is_active ?? merged.is_current ?? merged.active),
    /** Lab documents (UI copy: “documents”) */
    documentCount: merged.document_count ?? merged.documents_count ?? merged.n_documents ?? labN,
    /** Diagnostic documents / diagnoses (UI copy: “tests”) */
    labCount:
      merged.lab_count ?? merged.labs_count ?? merged.n_labs ?? merged.diag_document_count ?? diagN,
  };
}

function parseDetailResponse(response) {
  if (response == null) return null;
  if (Array.isArray(response)) {
    const first = response.find((x) => x && typeof x === 'object');
    return first ? parsePersonaSnapshot(first) : null;
  }
  if (typeof response !== 'object') return null;
  const inner = response.persona_snapshot ?? response.snapshot ?? response;
  return parsePersonaSnapshot(inner) ?? parsePersonaSnapshot(response);
}

const personaSnapshotsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /medapp/persona/snapshots/ */
    fetchPersonaSnapshots: builder.query({
      query: () => ({
        url: '/medapp/persona/snapshots/',
        method: 'GET',
      }),
      transformResponse: (response) =>
        normalizeListResponse(response)
          .map(coerceListRow)
          .filter(Boolean)
          .map(parsePersonaSnapshot)
          .filter(Boolean),
      providesTags: (result) => [
        { type: TAG_PERSONA_SNAPSHOTS, id: 'LIST' },
        ...(result ?? []).map((s) => ({ type: TAG_PERSONA_SNAPSHOTS, id: s.uuid })),
      ],
    }),

    /** GET /medapp/persona/snapshots/{snapshot_uuid}/ */
    fetchPersonaSnapshot: builder.query({
      query: (snapshotUuid) => ({
        url: `/medapp/persona/snapshots/${snapshotUuid}/`,
        method: 'GET',
      }),
      transformResponse: parseDetailResponse,
      providesTags: (result, error, snapshotUuid) => [
        { type: TAG_PERSONA_SNAPSHOTS, id: snapshotUuid },
        { type: TAG_PERSONA_SNAPSHOTS, id: 'LIST' },
      ],
    }),

    /**
     * POST /medapp/pubsub-persona-snapshot-create/
     * Body: { created, owner_id, options } — 402 = insufficient credits.
     */
    createPersonaSnapshot: builder.mutation({
      query: (arg = {}) => {
        const {
          ownerId,
          language = 'pl',
          mode = 'full',
          maxDateColumns = 5,
          defaultRecentDocumentCount = 5,
        } = arg;
        return {
          url: '/medapp/pubsub-persona-snapshot-create/',
          method: 'POST',
          body: {
            created: formatPubSubDate(),
            owner_id: ownerId,
            options: {
              language,
              mode,
              max_date_columns: maxDateColumns,
              default_recent_document_count: defaultRecentDocumentCount,
            },
          },
        };
      },
      invalidatesTags: [{ type: TAG_PERSONA_SNAPSHOTS, id: 'LIST' }],
    }),

    /** DELETE /medapp/persona/snapshots/{snapshot_uuid}/ — when the backend supports delete. */
    deletePersonaSnapshot: builder.mutation({
      query: (snapshotUuid) => ({
        url: `/medapp/persona/snapshots/${snapshotUuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, snapshotUuid) => [
        { type: TAG_PERSONA_SNAPSHOTS, id: 'LIST' },
        { type: TAG_PERSONA_SNAPSHOTS, id: snapshotUuid },
      ],
    }),
  }),
});

export const {
  useFetchPersonaSnapshotsQuery,
  useFetchPersonaSnapshotQuery,
  useLazyFetchPersonaSnapshotQuery,
  useCreatePersonaSnapshotMutation,
  useDeletePersonaSnapshotMutation,
} = personaSnapshotsApi;
